import { useEffect, useState, type ComponentProps } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/select';
import type { JsonValue } from '@/pages/workflow/helper/functional-control-flow';
import {
  coerceNodeToType,
  emptySchemaNode,
  SCHEMA_TYPE_LABELS,
  SCHEMA_TYPES,
  type SchemaNode,
  type SchemaProperty,
  type SchemaType,
} from '@/pages/workflow/helper/validate-builder';
import {
  formatTypedList,
  parseTypedList,
  type ValueKind,
} from '@/pages/workflow/helper/switch-condition-builder';
import { DraftInput } from './switch-cases-editor';
import { cn } from '@/lib/utils';

/**
 * The field table for a Validate node's shape (its JSON Schema). One row per
 * field: name, type, required, and the bounds that make sense for the type.
 * Objects nest their fields; lists describe "each item". Every edit writes
 * the bounded schema subset the backend accepts, and nothing else.
 */

const TYPE_OPTIONS = SCHEMA_TYPES.map((type) => SCHEMA_TYPE_LABELS[type]);
const ANY_TYPE_LABEL = 'Any';
const typeFromLabel = (label: string | undefined): SchemaType | null =>
  SCHEMA_TYPES.find((type) => SCHEMA_TYPE_LABELS[type] === label) ?? null;

const enumKindFor = (type: SchemaType | null): ValueKind =>
  type === 'number' || type === 'integer'
    ? 'number'
    : type === 'boolean'
      ? 'boolean'
      : 'text';

/** A small numeric input that commits a number on blur, or clears it. */
function NumberDraft({
  value,
  onCommit,
  integer = false,
  className,
  ...rest
}: Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> & {
  value: number | undefined;
  onCommit: (next: number | undefined) => void;
  integer?: boolean;
}) {
  const [draft, setDraft] = useState(value === undefined ? '' : String(value));
  useEffect(() => setDraft(value === undefined ? '' : String(value)), [value]);
  const [invalid, setInvalid] = useState(false);
  return (
    <Input
      {...rest}
      inputMode='decimal'
      value={draft}
      className={cn(
        'h-7 w-20 text-xs px-1.5',
        invalid && 'border-red-500',
        className,
      )}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        const text = draft.trim();
        if (text === '') {
          setInvalid(false);
          if (value !== undefined) onCommit(undefined);
          return;
        }
        const parsed = Number(text);
        const ok =
          Number.isFinite(parsed) && (!integer || Number.isInteger(parsed));
        setInvalid(!ok);
        if (ok && parsed !== value) onCommit(parsed);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          (event.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function Bound({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className='flex items-center gap-1 text-[11px] text-muted-foreground'>
      <span>{label}</span>
      {children}
    </label>
  );
}

interface NodeRulesProps {
  node: SchemaNode;
  disabled: boolean;
  onChange: (next: SchemaNode) => void;
}

/** The bounds and allowed values that apply to one schema node's type. */
function NodeRules({ node, disabled, onChange }: NodeRulesProps) {
  const set = (patch: Partial<SchemaNode>) => onChange({ ...node, ...patch });
  const numeric = node.type === 'number' || node.type === 'integer';
  const enumKind = enumKindFor(node.type);
  const [enumDraft, setEnumDraft] = useState(formatTypedList(node.enum ?? []));
  const [enumError, setEnumError] = useState<string | null>(null);
  useEffect(() => setEnumDraft(formatTypedList(node.enum ?? [])), [node.enum]);

  const showEnum =
    node.type === 'string' || numeric || node.type === 'boolean' || node.enum;
  const rows: React.ReactNode[] = [];
  if (node.type === 'string') {
    rows.push(
      <Bound key='minLength' label='length at least'>
        <NumberDraft
          integer
          value={node.minLength}
          disabled={disabled}
          aria-label='Minimum length'
          onCommit={(minLength) => set({ minLength })}
        />
      </Bound>,
      <Bound key='maxLength' label='at most'>
        <NumberDraft
          integer
          value={node.maxLength}
          disabled={disabled}
          aria-label='Maximum length'
          onCommit={(maxLength) => set({ maxLength })}
        />
      </Bound>,
    );
  }
  if (numeric) {
    rows.push(
      <Bound key='minimum' label='at least'>
        <NumberDraft
          value={node.minimum}
          disabled={disabled}
          aria-label='Minimum'
          onCommit={(minimum) => set({ minimum })}
        />
      </Bound>,
      <Bound key='maximum' label='at most'>
        <NumberDraft
          value={node.maximum}
          disabled={disabled}
          aria-label='Maximum'
          onCommit={(maximum) => set({ maximum })}
        />
      </Bound>,
    );
    if (
      node.exclusiveMinimum !== undefined ||
      node.exclusiveMaximum !== undefined
    ) {
      rows.push(
        <Bound key='exclusiveMinimum' label='more than'>
          <NumberDraft
            value={node.exclusiveMinimum}
            disabled={disabled}
            aria-label='Exclusive minimum'
            onCommit={(exclusiveMinimum) => set({ exclusiveMinimum })}
          />
        </Bound>,
        <Bound key='exclusiveMaximum' label='less than'>
          <NumberDraft
            value={node.exclusiveMaximum}
            disabled={disabled}
            aria-label='Exclusive maximum'
            onCommit={(exclusiveMaximum) => set({ exclusiveMaximum })}
          />
        </Bound>,
      );
    }
  }
  if (node.type === 'array') {
    rows.push(
      <Bound key='minItems' label='items at least'>
        <NumberDraft
          integer
          value={node.minItems}
          disabled={disabled}
          aria-label='Minimum items'
          onCommit={(minItems) => set({ minItems })}
        />
      </Bound>,
      <Bound key='maxItems' label='at most'>
        <NumberDraft
          integer
          value={node.maxItems}
          disabled={disabled}
          aria-label='Maximum items'
          onCommit={(maxItems) => set({ maxItems })}
        />
      </Bound>,
    );
  }
  if (node.type === 'object') {
    rows.push(
      <label
        key='additionalProperties'
        className='flex items-center gap-1.5 text-[11px] text-muted-foreground'
      >
        <Checkbox
          checked={node.additionalProperties === false}
          disabled={disabled}
          aria-label='No other fields allowed'
          onCheckedChange={(checked) =>
            set({ additionalProperties: checked === true ? false : undefined })
          }
        />
        no other fields allowed
      </label>,
    );
  }
  if (showEnum) {
    rows.push(
      <label
        key='enum'
        className='flex items-center gap-1 text-[11px] text-muted-foreground flex-1 min-w-[140px]'
      >
        <span className='shrink-0'>one of</span>
        <Input
          value={enumDraft}
          disabled={disabled}
          placeholder='comma-separated'
          aria-label='Allowed values'
          className={cn('h-7 text-xs px-1.5', enumError && 'border-red-500')}
          onChange={(event) => setEnumDraft(event.target.value)}
          onBlur={() => {
            const parsed = parseTypedList(enumDraft, enumKind);
            if (!parsed) {
              setEnumError(
                enumKind === 'number'
                  ? 'Enter numbers separated by commas.'
                  : 'Enter true or false values separated by commas.',
              );
              return;
            }
            setEnumError(null);
            const next = parsed.value.length ? parsed.value : null;
            if (JSON.stringify(next) !== JSON.stringify(node.enum)) {
              set({ enum: next });
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              (event.target as HTMLInputElement).blur();
            }
          }}
        />
      </label>,
    );
  }
  if (node.const !== undefined) {
    rows.push(
      <span
        key='const'
        className='flex items-center gap-1 text-[11px] text-muted-foreground'
      >
        must equal{' '}
        <code className='px-1 rounded bg-muted'>
          {JSON.stringify(node.const)}
        </code>
        {!disabled && (
          <button
            type='button'
            className='underline'
            onClick={() => set({ const: undefined })}
          >
            remove
          </button>
        )}
      </span>,
    );
  }
  if (rows.length === 0) return null;
  return (
    <div className='flex flex-wrap items-center gap-x-3 gap-y-1 pl-1'>
      {rows}
      {enumError && (
        <span className='w-full text-[11px] text-red-500'>{enumError}</span>
      )}
    </div>
  );
}

interface SchemaNodeEditorProps {
  node: SchemaNode;
  /** The property this node describes; absent for the root and for list items. */
  property?: { name: string; required: boolean };
  heading?: string;
  depth: number;
  disabled: boolean;
  onChange: (next: SchemaNode) => void;
  onRename?: (name: string) => void;
  onToggleRequired?: (required: boolean) => void;
  onRemove?: () => void;
}

export function SchemaNodeEditor({
  node,
  property,
  heading,
  depth,
  disabled,
  onChange,
  onRename,
  onToggleRequired,
  onRemove,
}: SchemaNodeEditorProps) {
  const setType = (type: SchemaType | null) =>
    onChange(coerceNodeToType(node, type));
  const children = node.properties ?? [];
  const updateChild = (index: number, patch: Partial<SchemaProperty>) =>
    onChange({
      ...node,
      properties: children.map((child, i) =>
        i === index ? { ...child, ...patch } : child,
      ),
    });

  return (
    <div
      className={cn('space-y-1', depth > 0 && 'border-l-2 border-muted pl-2')}
      data-testid='validate-schema-node'
    >
      <div className='flex items-center gap-1 flex-wrap'>
        {property ? (
          <DraftInput
            value={property.name}
            disabled={disabled}
            placeholder='field name'
            aria-label='Field name'
            className='h-7 w-[120px] font-mono'
            onCommit={(name) => onRename?.(name.trim())}
          />
        ) : (
          <span className='text-xs font-medium text-foreground'>
            {heading ?? 'The value'}
          </span>
        )}
        <Select
          options={[ANY_TYPE_LABEL, ...TYPE_OPTIONS]}
          selected={node.type ? SCHEMA_TYPE_LABELS[node.type] : ANY_TYPE_LABEL}
          allowAllOption={false}
          disabled={disabled}
          className='h-7 w-auto min-w-[96px] text-xs px-2'
          onSelect={(label) =>
            setType(label === ANY_TYPE_LABEL ? null : typeFromLabel(label))
          }
        />
        {property && onToggleRequired && (
          <label className='flex items-center gap-1 text-[11px] text-muted-foreground'>
            <Checkbox
              checked={property.required}
              disabled={disabled}
              aria-label={`${property.name || 'field'} is required`}
              onCheckedChange={(checked) => onToggleRequired(checked === true)}
            />
            required
          </label>
        )}
        {onRemove && !disabled && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 w-7 p-0 ml-auto text-muted-foreground'
            aria-label={`Remove ${property?.name || 'field'}`}
            onClick={onRemove}
          >
            <Trash2 className='size-3.5' />
          </Button>
        )}
      </div>
      <NodeRules node={node} disabled={disabled} onChange={onChange} />
      {node.type === 'object' && (
        <div className='space-y-1 pl-2'>
          {children.map((child, index) => (
            <SchemaNodeEditor
              key={index}
              node={child.node}
              property={{ name: child.name, required: child.required }}
              depth={depth + 1}
              disabled={disabled}
              onChange={(next) => updateChild(index, { node: next })}
              onRename={(name) => updateChild(index, { name })}
              onToggleRequired={(required) => updateChild(index, { required })}
              onRemove={() =>
                onChange({
                  ...node,
                  properties: children.filter((_, i) => i !== index),
                })
              }
            />
          ))}
          {!disabled && depth < 4 && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 px-2 text-xs'
              data-testid='validate-schema-add-field'
              onClick={() =>
                onChange({
                  ...node,
                  properties: [
                    ...children,
                    {
                      name: '',
                      required: true,
                      node: emptySchemaNode('string'),
                    },
                  ],
                })
              }
            >
              <Plus className='size-3' /> field
            </Button>
          )}
        </div>
      )}
      {node.type === 'array' && (
        <div className='pl-2'>
          {node.items ? (
            <SchemaNodeEditor
              node={node.items}
              heading='each item'
              depth={depth + 1}
              disabled={disabled}
              onChange={(items) => onChange({ ...node, items })}
              onRemove={() => onChange({ ...node, items: null })}
            />
          ) : (
            !disabled &&
            depth < 4 && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-6 px-2 text-xs'
                onClick={() =>
                  onChange({ ...node, items: emptySchemaNode('string') })
                }
              >
                <Plus className='size-3' /> describe each item
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
}

export type { JsonValue };
